import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { KeyboardEvent, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CHECKLIST_COLORS,
  CHECKLIST_ICONS,
} from 'src/Components/Checklists/checklistVisuals.helper';
import { IconColorPicker } from 'src/Components/IconColorPicker';

import styles from './ChecklistForm.module.scss';
import { Props } from './ChecklistForm.types';
import { ChecklistFormItem } from './ChecklistFormItem';

const COLOR_OPTIONS = Object.entries(CHECKLIST_COLORS).map(([key, hex]) => ({ key, hex }));

const ChecklistForm = ({
  onCancel,
  form,
  onSubmit,
  submitButtonLabel,
  addItem,
  removeItem,
  reorderItems,
}: Props) => {
  const { t } = useTranslation();
  const lastInputRef = useRef<HTMLInputElement>(null);

  const prevItemsLengthRef = useRef(form.values.items.length);

  useEffect(() => {
    if (form.values.items.length > prevItemsLengthRef.current) {
      lastInputRef.current?.focus();
    }
    prevItemsLengthRef.current = form.values.items.length;
  }, [form.values.items.length]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addItem();
    }
  };

  const handleDragEnd = (result: DragEndEvent) => {
    // We did finish a reordering drag
    const { active, over } = result;

    if (over && active.id !== over.id) {
      const oldIndex = form.values.items.findIndex(item => item.localId == active.id);
      const newIndex = form.values.items.findIndex(item => item.localId == over.id);

      reorderItems(oldIndex, newIndex);
    }
  };

  const itemError = form.errors['items'];

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const items = form.values.items.map((item, index) => {
    const isLastItem = index === form.values.items.length - 1;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isLastItem) handleKeyDown(event);
    };

    const onDelete = () => {
      removeItem(index);
    };

    return (
      <ChecklistFormItem
        key={item.localId}
        item={item}
        itemIndex={index}
        isLastItem={index === form.values.items.length - 1}
        form={form}
        onKeyDown={onKeyDown}
        inputRef={isLastItem ? lastInputRef : undefined}
        onDelete={onDelete}
      />
    );
  });

  return (
    <form id="checklist-form" onSubmit={onSubmit} className={styles.fullFormContainer}>
      <div className={styles.titleRow}>
        <IconColorPicker
          icons={CHECKLIST_ICONS}
          colors={COLOR_OPTIONS}
          icon={form.values.icon}
          color={form.values.color}
          onIconChange={value => form.setFieldValue('icon', value)}
          onColorChange={value => form.setFieldValue('color', value)}
        />
        <TextInput
          placeholder={t('checklist.form.creationTitle', 'Titre de la checklist ')}
          {...form.getInputProps('title')}
          className={styles.titleInput}
        />
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={form.values.items.map(item => item.localId)}
          strategy={verticalListSortingStrategy}
        >
          {items}
        </SortableContext>
      </DndContext>

      {itemError && <div className={styles.error}>{itemError}</div>}
      <Button
        variant={'transparent'}
        onClick={addItem}
        leftSection={<IconPlus size={20} />}
        className={styles.addItemButton}
      >
        {t('checklist.form.addItem', 'Ajouter une option')}
      </Button>
      <div className={styles.buttonsContainer}>
        <Button onClick={onCancel} variant="outline" className="btn-medium">
          {t('checklist.form.cancel', 'Annuler')}
        </Button>
        <Button type="submit" variant="primary">
          {submitButtonLabel}
        </Button>
      </div>
    </form>
  );
};

export default ChecklistForm;
